import React, { useCallback, useEffect, useState } from "react";
import { Wallet, TrendingDown, TrendingUp, Trash2, Loader2, FileSpreadsheet, FileDown, Plus } from "lucide-react";
import { api, fmtDate, KATEGORI, KATEGORI_EXTRA_PENGELUARAN, loadKategoriPengeluaran } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const today = () => new Date().toISOString().slice(0, 10);
const now = () => new Date().toTimeString().slice(0, 5);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

export const AdminReport = () => {
  const [range, setRange] = useState({ start: "", end: "" });
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [keluar, setKeluar] = useState({ tanggal: today(), waktu: now(), nomor: "", jumlah: "", deskripsi: "", akun: "KAS", kategori: "A3" });
  const [katOut, setKatOut] = useState([...KATEGORI, ...KATEGORI_EXTRA_PENGELUARAN]);

  useEffect(() => {
    loadKategoriPengeluaran().then((extra) => {
      setKatOut([...KATEGORI, ...extra]);
    });
  }, []);

  const load = useCallback(async () => {
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    const [s, l, n] = await Promise.all([
      api.get("/finance/summary", { params }),
      api.get("/finance", { params }),
      api.get("/finance/next-number"),
    ]);
    setSummary(s.data);
    setRows(l.data);
    setKeluar((k) => ({ ...k, nomor: n.data.nomor }));
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (jenis) => {
    const body = keluar;
    if (!body.jumlah || Number(body.jumlah) <= 0) return toast.error("Jumlah wajib diisi");
    setBusy(jenis);
    try {
      await api.post(`/finance/${jenis}`, { ...body, jumlah: Number(body.jumlah) });
      toast.success("Pengeluaran tersimpan");
      setKeluar({ tanggal: today(), waktu: now(), nomor: "", jumlah: "", deskripsi: "", akun: "KAS", kategori: "A3" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan transaksi");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Hapus transaksi ${t.nomor}?`)) return;
    await api.delete(`/finance/${t.id}`);
    toast.success("Transaksi dihapus");
    load();
  };

  const download = async (format) => {
    setBusy(format);
    try {
      const params = { format };
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const res = await api.get("/finance-export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-admin.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Laporan ${format.toUpperCase()} diunduh`);
    } catch {
      toast.error("Gagal mengunduh laporan");
    } finally {
      setBusy(null);
    }
  };

  const katRecap = React.useMemo(() => {
    const build = (jenis) => {
      const map = {};
      let total = 0;
      rows
        .filter((r) => r.jenis === jenis)
        .forEach((r) => {
          const key = r.kategori || "LAINNYA";
          const jml = Number(r.jumlah || 0);
          const it = (map[key] = map[key] || { kategori: key, total: 0, count: 0, KAS: 0, BANK: 0 });
          it.total += jml;
          it.count += 1;
          it[r.akun === "BANK" ? "BANK" : "KAS"] += jml;
          total += jml;
        });
      return { items: Object.values(map).sort((a, b) => b.total - a.total), total };
    };
    return build("keluar");
  }, [rows]);

  const masukRecap = React.useMemo(() => {
    const map = {};
    let total = 0;
    rows
      .filter((r) => r.jenis === "masuk")
      .forEach((r) => {
        const key = r.kategori || "LAINNYA";
        const jml = Number(r.jumlah || 0);
        const it = (map[key] = map[key] || { kategori: key, total: 0, count: 0, KAS: 0, BANK: 0 });
        it.total += jml;
        it.count += 1;
        it[r.akun === "BANK" ? "BANK" : "KAS"] += jml;
        total += jml;
      });
    return { items: Object.values(map).sort((a, b) => b.total - a.total), total };
  }, [rows]);

  const cards = [
    { key: "masuk", label: "Total Pemasukan", icon: TrendingUp, color: "text-emerald-300" },
    { key: "keluar", label: "Total Pengeluaran", icon: TrendingDown, color: "text-red-300" },
    { key: "saldo", label: "Saldo (Masuk - Keluar)", icon: Wallet, color: "text-blue-300" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-report">
      <div className="glass rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Dari Tanggal">
              <Input
                data-testid="finance-start-date"
                type="date"
                value={range.start}
                onChange={(e) => setRange({ ...range, start: e.target.value })}
                className="h-11 w-auto bg-slate-900/70 border-white/10"
              />
            </Field>
            <Field label="Sampai Tanggal">
              <Input
                data-testid="finance-end-date"
                type="date"
                value={range.end}
                onChange={(e) => setRange({ ...range, end: e.target.value })}
                className="h-11 w-auto bg-slate-900/70 border-white/10"
              />
            </Field>
            {(range.start || range.end) && (
              <button
                data-testid="finance-clear-range"
                onClick={() => setRange({ start: "", end: "" })}
                className="h-11 text-xs text-slate-400 hover:text-blue-300 transition-colors duration-200"
              >
                semua tanggal
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="finance-xlsx-btn"
              onClick={() => download("xlsx")}
              disabled={busy}
              className="h-11 rounded-xl bg-emerald-600/90 hover:bg-emerald-500 px-5 font-semibold"
            >
              {busy === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel</>}
            </Button>
            <Button
              data-testid="finance-pdf-btn"
              onClick={() => download("pdf")}
              disabled={busy}
              className="h-11 rounded-xl bg-red-600/80 hover:bg-red-500 px-5 font-semibold"
            >
              {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-2" /> PDF</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3" data-testid="finance-summary">
        {cards.map((c) => (
          <div key={c.key} className="glass rounded-2xl p-6 lift" data-testid={`finance-card-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                {c.label}
              </span>
              <c.icon className={`w-4 h-4 ${c.color}`} />
            </div>
            <div className={`mt-4 font-display text-2xl md:text-3xl font-light ${c.color}`}>
              {rp(summary?.[c.key]?.TOTAL)}
            </div>
            <div className="mt-3 flex gap-4 text-[11px] text-slate-400">
              <span>KAS: <b className="text-slate-200">{rp(summary?.[c.key]?.KAS)}</b></span>
              <span>BANK: <b className="text-slate-200">{rp(summary?.[c.key]?.BANK)}</b></span>
            </div>
          </div>
        ))}
      </div>

      <div className="glass rounded-2xl p-6 md:p-8" data-testid="finance-kategori-recap">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-xl font-semibold">Rekap Pengeluaran per Kategori</h3>
          <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
            Total: <b className="text-red-300">{rp(katRecap.total)}</b>
          </span>
        </div>
        {katRecap.items.length === 0 ? (
          <p className="mt-5 text-sm text-slate-500" data-testid="finance-kategori-empty">
            Belum ada pengeluaran pada periode ini.
          </p>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {katRecap.items.map((k) => (
              <div
                key={k.kategori}
                data-testid={`finance-kategori-${k.kategori}`}
                className="rounded-xl border border-white/10 bg-slate-900/60 p-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded-md border border-red-400/25 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-200">
                    {k.kategori}
                  </span>
                  <span className="text-[10px] text-slate-500">{k.count} trx</span>
                </div>
                <div className="mt-3 font-display text-lg font-light text-slate-100">{rp(k.total)}</div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-red-400/70"
                    style={{ width: `${katRecap.total ? (k.total / katRecap.total) * 100 : 0}%` }}
                  />
                </div>
                <div className="mt-2 flex gap-3 text-[10px] text-slate-400">
                  <span>KAS: <b className="text-slate-200">{rp(k.KAS)}</b></span>
                  <span>BANK: <b className="text-slate-200">{rp(k.BANK)}</b></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-6 md:p-8" data-testid="finance-masuk-recap">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-semibold text-emerald-300">
              Rekap Pemasukan per Kategori
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Otomatis dari pembayaran di Papan Pembayaran (Nota, Invoice, Struk) — tidak ada entri manual.
            </p>
          </div>
          <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
            Total: <b className="text-emerald-300">{rp(masukRecap.total)}</b>
          </span>
        </div>
        {masukRecap.items.length === 0 ? (
          <p className="mt-5 text-sm text-slate-500" data-testid="finance-masuk-empty">
            Belum ada pemasukan pada periode ini. Catat pembayaran di Papan Pembayaran.
          </p>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {masukRecap.items.map((k) => (
              <div
                key={k.kategori}
                data-testid={`finance-masuk-kategori-${k.kategori}`}
                className="rounded-xl border border-white/10 bg-slate-900/60 p-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded-md border border-emerald-400/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-200">
                    {k.kategori}
                  </span>
                  <span className="text-[10px] text-slate-500">{k.count} trx</span>
                </div>
                <div className="mt-3 font-display text-lg font-light text-slate-100">{rp(k.total)}</div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-400/70"
                    style={{ width: `${masukRecap.total ? (k.total / masukRecap.total) * 100 : 0}%` }}
                  />
                </div>
                <div className="mt-2 flex gap-3 text-[10px] text-slate-400">
                  <span>KAS: <b className="text-slate-200">{rp(k.KAS)}</b></span>
                  <span>BANK: <b className="text-slate-200">{rp(k.BANK)}</b></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-6">
        <div className="glass rounded-2xl p-6 md:p-8" data-testid="form-pengeluaran">
          <h3 className="font-display text-xl font-semibold text-red-300">B. Pengeluaran</h3>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Tanggal">
              <Input data-testid="out-date" type="date" value={keluar.tanggal} onChange={(e) => setKeluar({ ...keluar, tanggal: e.target.value })} className="h-11 bg-slate-900/70 border-white/10" />
            </Field>
            <Field label="Waktu">
              <Input data-testid="out-time" type="time" value={keluar.waktu} onChange={(e) => setKeluar({ ...keluar, waktu: e.target.value })} className="h-11 bg-slate-900/70 border-white/10" />
            </Field>
            <Field label="Nomor Pengeluaran (otomatis)">
              <Input data-testid="out-nomor" value={keluar.nomor} readOnly className="h-11 bg-slate-950/80 border-white/10 text-blue-300" />
            </Field>
            <Field label="Kategori">
              <select data-testid="out-kategori" value={keluar.kategori} onChange={(e) => setKeluar({ ...keluar, kategori: e.target.value })} className="h-11 w-full rounded-xl bg-slate-900/70 border border-white/10 px-4 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                {katOut.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
            </Field>
            <Field label="Jumlah (Rp)">
              <Input data-testid="out-jumlah" type="number" value={keluar.jumlah} onChange={(e) => setKeluar({ ...keluar, jumlah: e.target.value })} placeholder="50000" className="h-11 bg-slate-900/70 border-white/10" />
            </Field>
            <Field label="Keterangan">
              <select data-testid="out-akun" value={keluar.akun} onChange={(e) => setKeluar({ ...keluar, akun: e.target.value })} className="h-11 w-full rounded-xl bg-slate-900/70 border border-white/10 px-4 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                <option value="KAS">KAS</option>
                <option value="BANK">BANK</option>
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Deskripsi">
                <Input data-testid="out-deskripsi" value={keluar.deskripsi} onChange={(e) => setKeluar({ ...keluar, deskripsi: e.target.value })} placeholder="beli bahan flexi" className="h-11 bg-slate-900/70 border-white/10" />
              </Field>
            </div>
          </div>
          <Button data-testid="out-submit" onClick={() => save("keluar")} disabled={busy} className="mt-6 h-12 w-full rounded-xl bg-red-600/90 hover:bg-red-500 font-semibold">
            {busy === "keluar" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Simpan Pengeluaran</>}
          </Button>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="p-5 md:p-6 border-b border-white/10">
          <h3 className="font-display text-xl font-semibold">
            Riwayat Transaksi <span className="text-slate-500 text-sm font-normal">({rows.length})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="finance-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-slate-400">
                <th className="px-5 py-4 font-semibold">Tanggal</th>
                <th className="px-5 py-4 font-semibold">Waktu</th>
                <th className="px-5 py-4 font-semibold">Jenis</th>
                <th className="px-5 py-4 font-semibold">Nomor</th>
                <th className="px-5 py-4 font-semibold">Kategori</th>
                <th className="px-5 py-4 font-semibold">Jumlah</th>
                <th className="px-5 py-4 font-semibold">Ket.</th>
                <th className="px-5 py-4 font-semibold">Oleh</th>
                <th className="px-5 py-4 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-slate-500">
                    Belum ada transaksi pada periode ini.
                  </td>
                </tr>
              )}
              {rows.map((t) => (
                <tr key={t.id} data-testid={`finance-row-${t.nomor}`} className="border-t border-white/5 hover:bg-blue-500/5 transition-colors duration-200">
                  <td className="px-5 py-3.5 text-slate-300 whitespace-nowrap">{fmtDate(t.tanggal)}</td>
                  <td className="px-5 py-3.5 text-slate-400">{t.waktu || "-"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                      t.jenis === "masuk"
                        ? "border-emerald-400/25 bg-emerald-500/10 text-emerald-300"
                        : "border-red-400/25 bg-red-500/10 text-red-300"
                    }`}>
                      {t.jenis === "masuk" ? "Masuk" : "Keluar"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-200">{t.nomor}</td>
                  <td className="px-5 py-3.5 text-slate-400">{t.kategori || "-"}</td>
                  <td className="px-5 py-3.5 font-display text-slate-100">{rp(t.jumlah)}</td>
                  <td className="px-5 py-3.5 text-slate-400">{t.akun}</td>
                  <td className="px-5 py-3.5 text-slate-400">{t.created_by}</td>
                  <td className="px-5 py-3.5 text-right">
                    <Button
                      data-testid={`finance-delete-${t.nomor}`}
                      onClick={() => remove(t)}
                      variant="outline"
                      className="h-9 w-9 p-0 rounded-lg border-red-400/20 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
